import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { ageFromBirthDate } from "../lib/format";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { ATTIVITA_OPTIONS, MEZZO_OPTIONS, MOBILIZZAZIONE_OPTIONS, CAUSALE_OPTIONS } from "../lib/options";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { emptyLog } from "../components/request-form/fields";
import { useAuth } from "../context/AuthContext";
import { EngagementSection } from "../components/request-form/EngagementSection";
import { SendModeCard } from "../components/request-form/SendModeCard";
import { RecurrenceSection } from "../components/request-form/RecurrenceSection";
import { PatientSection } from "../components/request-form/PatientSection";
import { OperativeSection } from "../components/request-form/OperativeSection";
import { Logistica } from "../components/request-form/Logistica";
import { ContabilitaSection, oreFM, feeNexus } from "../components/request-form/ContabilitaSection";

export { oreFM };

// Propone data/ora del rientro: stessa data con l'orario visita/fine se valido, altrimenti +3h dall'andata.
export const proposeReturnDateTime = (dataOra, orarioVisita) => {
  if (!dataOra) return "";
  const base = new Date(String(dataOra).replace(" ", "T"));
  if (isNaN(base.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const m = String(orarioVisita || "").match(/(\d{1,2})[:.](\d{2})/);
  if (m) {
    const d = new Date(base); d.setHours(Number(m[1]), Number(m[2]), 0, 0);
    if (d > base) return fmt(d);
  }
  return fmt(new Date(base.getTime() + 3 * 3600000));
};

export default function CreateRequest() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const duplicaId = searchParams.get("duplica");
  const preventivoId = searchParams.get("preventivo");
  const strutturaId = searchParams.get("struttura");
  const [srCode, setSrCode] = useState("");
  const isRitorno = searchParams.get("ritorno") === "1";
  const [dupCode, setDupCode] = useState("");
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("fcfs");
  const [assigned, setAssigned] = useState(false);
  const [patient, setPatient] = useState({ nominativo: "", anni: "", peso: "", telefono: "", indirizzo: "", nato_a: "", nato_il: "", codice_fiscale: "", email: "" });
  const [operative, setOperative] = useState({
    referente: (!id && user?.operatore_co) || "", provenienza: "", attivita: "", tipologia_mezzo: "", causale: "",
    necessita: "", mobilizzazione: "", tratta: "", data_ora_evento: "", orario_visita: "", richiesta_fm: false, minuti_fm: "",
  });
  const [partenza, setPartenza] = useState({ ...emptyLog });
  const [arrivo, setArrivo] = useState({ ...emptyLog });
  const [contabilita, setContabilita] = useState({
    importo: "", importo_totale: "", incasso: "", modalita_pagamento: "", fee_nexus: "",
    codice_fiscale: "", note: "", ora_arrivo: "", ora_ripartenza: "", km_manuali: "",
  });
  const [isCompleted, setIsCompleted] = useState(false);
  const [editCode, setEditCode] = useState("");
  const [mezzoOptions, setMezzoOptions] = useState(MEZZO_OPTIONS);
  const [mobOptions, setMobOptions] = useState(MOBILIZZAZIONE_OPTIONS);
  const [attivitaOptions, setAttivitaOptions] = useState(ATTIVITA_OPTIONS);
  const [causaleOptions, setCausaleOptions] = useState(CAUSALE_OPTIONS);
  const [editReason, setEditReason] = useState("");
  const [recurrence, setRecurrence] = useState({ frequency: "none", count: 4 });
  const [partners, setPartners] = useState([]);
  const [directPartnerId, setDirectPartnerId] = useState("");
  const [sendMode, setSendMode] = useState("all");
  const [sendIds, setSendIds] = useState([]);
  const [sendListIds, setSendListIds] = useState([]);
  const [richiedenti, setRichiedenti] = useState([]);
  const [distanzaKm, setDistanzaKm] = useState("");
  const [savePartenza, setSavePartenza] = useState(true);
  const [saveArrivo, setSaveArrivo] = useState(true);
  const locsRef = useRef({ par: null, arr: null });

  const computeDistance = async () => {
    const { par, arr } = locsRef.current;
    if (!par || !arr) return;
    try {
      const { data } = await api.post("/maps/distance", { origin: par, destination: arr });
      if (data && data.km != null) {
        setDistanzaKm(String(data.km));
        toast.success(data.approx
          ? `Distanza (in linea d'aria): ${data.km} km`
          : `Distanza tratta calcolata: ${data.km} km`);
      }
    } catch {
      toast.warning("Distanza non calcolata", { description: "Inseriscila manualmente se necessario." });
    }
  };

  const handleGeo = (which, loc) => {
    locsRef.current[which] = loc;
    computeDistance();
  };

  useEffect(() => {
    if (!isEdit) api.get("/partners").then(({ data }) => setPartners(data)).catch(() => {});
  }, [isEdit]);

  useEffect(() => {
    api.get("/richiedenti").then(({ data }) => setRichiedenti(data)).catch(() => {});
    api.get("/options", { params: { type: "mezzo", active_only: true } }).then(({ data }) => data.length && setMezzoOptions(data.map((o) => o.nome))).catch(() => {});
    api.get("/options", { params: { type: "mobilizzazione", active_only: true } }).then(({ data }) => data.length && setMobOptions(data.map((o) => o.nome))).catch(() => {});
    api.get("/options", { params: { type: "attivita", active_only: true } }).then(({ data }) => data.length && setAttivitaOptions(data.map((o) => o.nome))).catch(() => {});
    api.get("/options", { params: { type: "causale", active_only: true } }).then(({ data }) => data.length && setCausaleOptions(data.map((o) => o.nome))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!preventivoId || isEdit) return;
    api.get(`/amministrazione/preventivi/${preventivoId}`).then(({ data: pv }) => {
      const punti = (pv.punti || []).filter((x) => x.indirizzo);
      const MEZZO = { ambulanza_2op: "Ambulanza", pulmino_2op: "Pulmino Attrezzato", auto_1op: "Auto Medica", cmr: "Ambulanza" };
      setOperative((o) => ({
        ...o, tipologia_mezzo: MEZZO[pv.mezzo] || o.tipologia_mezzo,
        tratta: ["urbana_singola", "extraurbana"].includes(pv.tratta) ? "Andata" : "Andata e Ritorno",
        data_ora_evento: pv.data_trasporto ? `${pv.data_trasporto}T08:00` : o.data_ora_evento,
        necessita: [pv.medico && "Medico a bordo", pv.infermiere && "Infermiere a bordo"].filter(Boolean).join(", ") || o.necessita,
      }));
      if (punti[0]) setPartenza((l) => ({ ...l, indirizzo: punti[0].indirizzo, citta: punti[0].indirizzo.split(",").slice(-1)[0].trim() }));
      if (punti.length > 1) setArrivo((l) => ({ ...l, indirizzo: punti[punti.length - 1].indirizzo, citta: punti[punti.length - 1].indirizzo.split(",").slice(-1)[0].trim() }));
      const paz = pv.paziente || (pv.cliente?.nominativo ? { nominativo: pv.cliente.nominativo } : null);
      if (paz) setPatient((p) => ({ ...p, ...Object.fromEntries(Object.entries(paz).filter(([, v]) => v)) }));
      setDistanzaKm(pv.km ? String(pv.km) : "");
      setContabilita((c) => ({ ...c, importo: String(pv.calcolo?.totale ?? ""), fee_nexus: feeNexus(pv.calcolo?.totale), note: `Da preventivo ${pv.codice}${pv.note ? ` — ${pv.note}` : ""}` }));
      toast.info(`Dati precompilati dal preventivo ${pv.codice}`, { description: "Completa paziente e dettagli, poi pubblica la richiesta." });
    }).catch(() => toast.error("Preventivo non trovato"));
  }, [preventivoId, isEdit]);

  useEffect(() => {
    if (!strutturaId || isEdit) return;
    api.get(`/strutture/richieste/${strutturaId}`).then(({ data: sr }) => {
      const citta = (addr) => {
        const parts = (addr || "").split(",").map((x) => x.trim()).filter(Boolean);
        const seg = parts.length > 1 ? parts[parts.length - 2] : parts[0] || "";
        return seg.replace(/^\d{5}\s*/, "").replace(/\s+[A-Z]{2}$/, "");
      };
      setSrCode(sr.code);
      setPatient((p) => ({ ...p, nominativo: sr.paziente_nominativo, nato_il: sr.paziente_nato_il || p.nato_il, anni: ageFromBirthDate(sr.paziente_nato_il) || p.anni, peso: sr.paziente_peso || p.peso, codice_fiscale: sr.paziente_cf || p.codice_fiscale }));
      setOperative((o) => ({ ...o, provenienza: sr.struttura_nome || o.provenienza, data_ora_evento: sr.data_ora_carico || o.data_ora_evento, orario_visita: sr.orario_visita || o.orario_visita, mobilizzazione: sr.mobilizzazione || o.mobilizzazione, tratta: sr.andata_ritorno ? "Andata e Ritorno" : "Andata", tipologia_mezzo: sr.mezzo || o.tipologia_mezzo, causale: sr.motivo || o.causale, richiesta_fm: !!sr.fermo_macchina }));
      const partAddr = sr.partenza_indirizzo || sr.struttura_indirizzo;
      setPartenza((l) => ({ ...l, indirizzo: partAddr || l.indirizzo, citta: citta(partAddr) || l.citta, reparto_citofono: sr.reparto_partenza || l.reparto_citofono }));
      setArrivo((l) => ({ ...l, indirizzo: sr.destinazione_indirizzo || sr.destinazione_nome, citta: citta(sr.destinazione_indirizzo) || l.citta, reparto_citofono: sr.reparto_destinazione || "" }));
      setContabilita((c) => ({ ...c, note: [`Da Portale Strutture ${sr.code}`, sr.struttura_partenza && `Partenza: ${sr.struttura_partenza}`, sr.destinazione_nome && `Destinazione: ${sr.destinazione_nome}`, sr.note_motivo && `Note motivo: ${sr.note_motivo}`, sr.note && `Note struttura: ${sr.note}`].filter(Boolean).join(" — ") }));
      toast.info(`Dati precompilati dalla richiesta ${sr.code}`, { description: "Completa mezzo e dettagli, poi pubblica ai partner." });
    }).catch(() => toast.error("Richiesta struttura non trovata"));
  }, [strutturaId, isEdit]);

  useEffect(() => {
    const srcId = isEdit ? id : duplicaId;
    if (!srcId) return;
    api.get(`/requests/${srcId}`).then(({ data }) => {
      if (isEdit) {
        setEditCode(data.code || "");
        setAssigned(data.status !== "pubblicata");
        setIsCompleted(["completata", "da_fatturare"].includes(data.status));
        setMode(data.engagement_mode || "fcfs");
      } else {
        setDupCode(data.code || "");
        setMode(data.engagement_mode === "direct" ? "fcfs" : (data.engagement_mode || "fcfs"));
      }
      setPatient({ ...emptyLog, ...(data.patient || {}) });
      const op = data.operative || {};
      const ritorno = !isEdit && isRitorno;
      setOperative((o) => ritorno
        ? { ...o, ...op, tratta: "Andata", orario_visita: "", data_ora_evento: proposeReturnDateTime(op.data_ora_evento, op.orario_visita) }
        : { ...o, ...op });
      setPartenza({ ...emptyLog, ...((ritorno ? data.arrivo : data.partenza) || {}) });
      setArrivo({ ...emptyLog, ...((ritorno ? data.partenza : data.arrivo) || {}) });
      setContabilita((c) => ({ ...c, ...(data.contabilita || {}), ora_arrivo: "", ora_ripartenza: "" }));
      setDistanzaKm(data.distanza_km || "");
      if (ritorno) toast.info(`Ritorno di ${data.code}`, { description: "Partenza e arrivo invertiti; orario di rientro proposto. Verifica e pubblica: nuovo numero di servizio." });
      else if (!isEdit) toast.info(`Scheda ${data.code} duplicata`, { description: "Controlla data/ora e i dati prima di pubblicare: verrà assegnato un nuovo numero di servizio." });
    }).catch(() => toast.error("Impossibile caricare la richiesta"));
  }, [id, isEdit, duplicaId, isRitorno]);

  const submit = async () => {
    if (isEdit && isCompleted && !editReason.trim()) {
      toast.error("Motivazione obbligatoria", { description: "Indica il motivo della modifica su una scheda completata." });
      return;
    }
    setLoading(true);
    try {
      const payload = { engagement_mode: mode, patient, operative, partenza, arrivo, contabilita, distanza_km: distanzaKm, save_partenza: savePartenza, save_arrivo: saveArrivo, edit_reason: editReason.trim() };
      if (isEdit) {
        const { data } = await api.put(`/requests/${id}`, payload);
        toast.success(`Richiesta ${data.code} aggiornata`);
        navigate(`/operativa/richiesta/${data.id}`);
      } else {
        if (strutturaId) payload.struttura_request_id = strutturaId;
        if (recurrence.frequency !== "none") {
          payload.recurrence = { frequency: recurrence.frequency, count: recurrence.count };
        }
        if (mode === "direct") {
          if (!directPartnerId) { toast.error("Seleziona il partner per l'assegnazione diretta"); setLoading(false); return; }
          payload.direct_partner_id = directPartnerId;
        } else if (sendMode === "lista") {
          if (!sendIds.length) { toast.error("Seleziona almeno un partner o una lista a cui inviare la richiesta"); setLoading(false); return; }
          Object.assign(payload, { send_mode: "lista", partner_ids: sendIds, list_ids: sendListIds });
        }
        const { data } = await api.post("/requests", payload);
        const n = data.created_count || 1;
        toast.success(
          n > 1 ? `${n} corse ricorrenti pubblicate` : `Richiesta ${data.code} pubblicata`,
          { description: sendMode === "lista" ? `Inviata a ${sendIds.length} partner selezionati.` : "I partner sono stati notificati via email." }
        );
        navigate(`/operativa/richiesta/${data.id}`);
      }
    } catch (err) {
      toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  const richiedenteOptions = [...new Set([
    ...richiedenti.map((r) => r.nome),
    ...(operative.provenienza ? [operative.provenienza] : []),
  ])];

  const formOptions = { mezzo: mezzoOptions, mobilizzazione: mobOptions, attivita: attivitaOptions, causale: causaleOptions };
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/operativa"));
  const title = isEdit ? "Modifica Richiesta di Trasporto" : srCode ? `Richiesta ${srCode} — Nuovo Trasporto` : dupCode ? (isRitorno ? `Ritorno di ${dupCode} — Nuova Richiesta` : `Duplica ${dupCode} — Nuova Richiesta`) : "Nuova Richiesta di Trasporto";

  return (
    <AppLayout crumbLabel={editCode}>
      <div className="max-w-4xl mx-auto nx-fade-in pb-10">
        <Button variant="ghost" size="sm" className="gap-1 mb-3 -ml-2" onClick={goBack}><ArrowLeft className="h-4 w-4" /> Indietro</Button>
        <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1">{title}</h1>
        <p className="text-sm text-slate-500 mb-6">{isEdit ? "Aggiorna i dati della prenotazione" : "Compila le sezioni per pubblicare la richiesta ai Partner"}</p>

        <EngagementSection mode={mode} setMode={setMode} disabled={assigned} isEdit={isEdit} partners={partners} directPartnerId={directPartnerId} setDirectPartnerId={setDirectPartnerId} />
        {!isEdit && mode !== "direct" && <SendModeCard sendMode={sendMode} setSendMode={setSendMode} partners={partners} selectedIds={sendIds} setSelectedIds={setSendIds} selectedListIds={sendListIds} setSelectedListIds={setSendListIds} />}
        {!isEdit && <RecurrenceSection recurrence={recurrence} setRecurrence={setRecurrence} />}

        <div className="space-y-5">
          <PatientSection patient={patient} setPatient={setPatient} />
          <OperativeSection operative={operative} setOperative={setOperative} options={formOptions} richiedenteOptions={richiedenteOptions} />
          <Logistica title="Logistica - Partenza" data={partenza} set={(k, v) => setPartenza({ ...partenza, [k]: v })} setAll={setPartenza} prefix="par" onGeo={(loc) => handleGeo("par", loc)} saveAddr={savePartenza} setSaveAddr={setSavePartenza} />
          <Logistica title="Logistica - Arrivo" data={arrivo} set={(k, v) => setArrivo({ ...arrivo, [k]: v })} setAll={setArrivo} prefix="arr" onGeo={(loc) => handleGeo("arr", loc)} saveAddr={saveArrivo} setSaveAddr={setSaveArrivo} />
          <ContabilitaSection contabilita={contabilita} setContabilita={setContabilita} distanzaKm={distanzaKm} setDistanzaKm={setDistanzaKm} />
        </div>

        <Separator className="my-6" />
        {isEdit && isCompleted && (
          <Card data-testid="edit-reason-card" className="p-5 mb-6 border-amber-300 bg-amber-50/60">
            <Label className="nx-label text-amber-800">Motivazione modifica (obbligatoria — scheda completata)</Label>
            <Textarea data-testid="edit-reason" value={editReason} onChange={(e) => setEditReason(e.target.value)} rows={2}
              placeholder="Es. correzione importo dopo verifica fattura" className="mt-1.5 bg-white" />
            <p className="text-[11px] text-amber-700 mt-1.5">La modifica e la motivazione saranno registrate nel log della scheda con data e operatore.</p>
          </Card>
        )}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={goBack}>Annulla</Button>
          <Button data-testid="submit-request" onClick={submit} disabled={loading} className="gap-2 min-w-[180px]">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Salva Modifiche" : "Pubblica Richiesta")}
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
